import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAllSignals, listTargetAccounts, previewAccountScores } from '../api/client';
import { useScoringConfig } from '../scoring/ScoringConfigContext';
import ConsoleRail from '../components/ConsoleRail';
import AccountDrawer from '../components/AccountDrawer';
import IngestionControl from '../components/IngestionControl';
import AddAccountModal from '../components/AddAccountModal';
import Pager, { PAGE_SIZE } from '../components/Pager';
import { timeAgo, eventDate, isEventDateEstimated } from '../familyMeta';
import { COLLECTOR_SOURCE_IDS, collectorSourceOptions, sourceLabel } from '../sourceMeta';
import { Plus } from 'lucide-react';

const ACCOUNT_FILTERS = [
  { value: 'all', label: 'All accounts' },
  { value: 'watchlisted', label: 'Watchlist' },
];

function tierRangeLabel(tier, config) {
  if (tier === 'A') return `${config.tierAMin}-100`;
  if (tier === 'B') return `${config.tierBMin}-${config.tierAMin - 1}`;
  return `0-${config.tierBMin - 1}`;
}

export default function Console() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { config, sliderRanges, updateWeight, updateField, resetToDefaults, justSaved } =
    useScoringConfig();

  const [signals, setSignals] = useState(null);
  const [scoreItems, setScoreItems] = useState(null);
  const [error, setError] = useState(null);
  const [scoringError, setScoringError] = useState(null);
  const [scoringLoading, setScoringLoading] = useState(false);
  const [enabledSources, setEnabledSources] = useState(null); // null = "all" until we know the sources
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'score', dir: 'desc' });
  const [accountFilter, setAccountFilter] = useState('all');
  const [targetAccounts, setTargetAccounts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [tierFilter, setTierFilter] = useState('all');

  const loadSignals = useCallback(() => {
    getAllSignals({ sources: COLLECTOR_SOURCE_IDS })
      .then((items) => {
        setSignals(items);
        setEnabledSources((prev) => prev || new Set(COLLECTOR_SOURCE_IDS));
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadTargetAccounts = useCallback(() => {
    listTargetAccounts({})
      .then(setTargetAccounts)
      .catch(() => setTargetAccounts([])); // non-fatal — watchlist tagging just won't show
  }, []);

  useEffect(() => {
    loadSignals();
    loadTargetAccounts();
  }, [loadSignals, loadTargetAccounts]);

  const latestObservedAt = useMemo(() => {
    if (!signals || signals.length === 0) return null;
    return signals.reduce((max, s) => (s.observed_at > max ? s.observed_at : max), signals[0].observed_at);
  }, [signals]);

  const sourceOptions = useMemo(() => {
    if (!signals) return [];
    const counts = {};
    for (const s of signals) counts[s.source] = (counts[s.source] || 0) + 1;
    return collectorSourceOptions(counts);
  }, [signals]);

  const filteredSignals = useMemo(() => {
    if (!signals || !enabledSources) return [];
    return signals.filter((s) => enabledSources.has(s.source));
  }, [signals, enabledSources]);

  // Watchlisted accounts (§4: tag + label in the same table, not a separate
  // screen) — matched by domain since a fresh target account has no signals,
  // and therefore no backend Account row, until its first check finds
  // something.
  const watchlistByDomain = useMemo(() => {
    const map = new Map();
    // Every target account is shown regardless of status — pausing hides an
    // account from what "Run watchlist" checks (backend filters to active
    // there), it must never hide the account from view here.
    for (const t of targetAccounts) {
      const domain = (t.company_domain || '').toLowerCase();
      if (domain) map.set(domain, t);
    }
    return map;
  }, [targetAccounts]);

  useEffect(() => {
    if (!signals || !enabledSources) return undefined;
    let cancelled = false;
    setScoringLoading(true);
    setScoringError(null);
    const timer = setTimeout(() => {
      previewAccountScores(config, { sources: [...enabledSources] })
        .then((response) => {
          if (!cancelled) setScoreItems(response.items);
        })
        .catch((e) => {
          if (!cancelled) setScoringError(e.message);
        })
        .finally(() => {
          if (!cancelled) setScoringLoading(false);
        });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [signals, enabledSources, config]);

  const scoredAccounts = useMemo(() => {
    if (!scoreItems) return [];
    const rows = scoreItems.map((item) => {
      const target = watchlistByDomain.get((item.account.domain || '').toLowerCase());
      return {
        accountId: item.account.id,
        accountName: item.account.name,
        account: item.account,
        isWatchlisted: !!target,
        targetAccount: target || null,
        score: item.score,
        tier: item.tier,
        contrib: item.family_contributions,
        corroborated: item.corroborated,
        distinctFamilies: item.distinct_recent_families,
        countByFamily: item.count_by_family,
        signalCount: item.signal_count,
        lastSignal: item.last_signal_id
          ? {
              id: item.last_signal_id,
              published_at: item.last_signal_at,
              observed_at: item.last_signal_at,
              signal_type: item.last_signal_type,
              source: item.last_signal_source,
            }
          : null,
      };
    });

    // Watchlist accounts with zero signals so far still need to show up
    // immediately (§4) — synthesize a pending row rather than waiting for a
    // signal to create the backend Account.
    const coveredDomains = new Set(
      rows.map((r) => (r.account.domain || '').toLowerCase()).filter(Boolean)
    );
    for (const [domain, t] of watchlistByDomain) {
      if (coveredDomains.has(domain)) continue;
      rows.push({
        accountId: `watchlist-${t.id}`,
        accountName: t.company_name,
        account: { id: null, domain: t.company_domain },
        isWatchlisted: true,
        targetAccount: t,
        pending: true,
        score: 0,
        tier: 'C',
        contrib: {},
        corroborated: false,
        distinctFamilies: 0,
        signalCount: 0,
        lastSignal: null,
      });
    }
    return rows;
  }, [scoreItems, watchlistByDomain]);

  const unattributedCount = useMemo(
    () => filteredSignals.filter((s) => !s.account).length,
    [filteredSignals]
  );

  const watchlistStats = useMemo(() => {
    const activeCount = targetAccounts.filter((t) => t.status === 'active').length;
    const pausedCount = targetAccounts.length - activeCount;
    const lastCheckedAt = targetAccounts.reduce(
      (max, t) => (t.last_checked_at && (!max || t.last_checked_at > max) ? t.last_checked_at : max),
      null
    );
    return { activeCount, pausedCount, lastCheckedAt };
  }, [targetAccounts]);

  const visibleAccounts = useMemo(() => {
    let list = scoredAccounts;
    if (accountFilter === 'watchlisted') list = list.filter((a) => a.isWatchlisted);
    if (tierFilter !== 'all') list = list.filter((a) => a.tier === tierFilter);
    if (search) list = list.filter((a) => a.accountName.toLowerCase().includes(search.toLowerCase()));
    list = [...list];
    const asc = sort.dir === 'asc';
    if (sort.key === 'score') list.sort((a, b) => (asc ? a.score - b.score : b.score - a.score));
    else if (sort.key === 'recent') {
      list.sort((a, b) => {
        if (!a.lastSignal && !b.lastSignal) return 0;
        if (!a.lastSignal) return 1; // pending (no signals yet) always sorts last
        if (!b.lastSignal) return -1;
        const diff = new Date(eventDate(b.lastSignal)) - new Date(eventDate(a.lastSignal));
        return asc ? -diff : diff;
      });
    } else if (sort.key === 'signals') list.sort((a, b) => (asc ? a.signalCount - b.signalCount : b.signalCount - a.signalCount));
    else list.sort((a, b) => (asc ? a.accountName.localeCompare(b.accountName) : b.accountName.localeCompare(a.accountName)));
    return list;
  }, [scoredAccounts, search, sort, accountFilter, tierFilter]);

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, sort, accountFilter, tierFilter]);
  const lastPage = Math.max(1, Math.ceil(visibleAccounts.length / PAGE_SIZE));
  const shownPage = Math.min(page, lastPage);
  const pageAccounts = visibleAccounts.slice((shownPage - 1) * PAGE_SIZE, shownPage * PAGE_SIZE);

  function toggleSort(key) {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // name defaults to A–Z (asc); everything else defaults to high-to-low (desc)
      return { key, dir: key === 'name' ? 'asc' : 'desc' };
    });
  }

  function toggleSource(source) {
    setEnabledSources((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  }

  if (error) {
    return <div className="state-msg error">Couldn't load signals: {error}</div>;
  }
  if (scoringError) {
    return <div className="state-msg error">Couldn't calculate scores: {scoringError}</div>;
  }
  if (!signals || !scoreItems) {
    return <div className="state-msg">Loading…</div>;
  }

  return (
    <>
      <div className="workspace">
        <ConsoleRail
          sourceOptions={sourceOptions}
          enabledSources={enabledSources || new Set()}
          onToggleSource={toggleSource}
          config={config}
          sliderRanges={sliderRanges}
          updateWeight={updateWeight}
          updateField={updateField}
          resetToDefaults={resetToDefaults}
          justSaved={justSaved}
        />

        <main>
          <IngestionControl
            latestObservedAt={latestObservedAt}
            onRunComplete={() => { loadSignals(); loadTargetAccounts(); }}
            showWatchlistRun={accountFilter === 'watchlisted' && targetAccounts.length > 0}
          />

          <div className="stats-strip">
            <div className="stat">
              <div className="num">{scoringLoading ? '—' : scoredAccounts.length}</div>
              <div className="lbl">Accounts tracked</div>
            </div>
            <div className="stat">
              <div className="num">{filteredSignals.length}</div>
              <div className="lbl">Signals ingested{unattributedCount ? ` (${unattributedCount} unattributed)` : ''}</div>
            </div>
            <div className="stat">
              <div className="num">{enabledSources.size} / {sourceOptions.length}</div>
              <div className="lbl">Sources selected</div>
            </div>
          </div>

          <div className="filter-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search accounts…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="account-filter-tabs" role="tablist" aria-label="Filter accounts">
              {ACCOUNT_FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="tab"
                  aria-selected={accountFilter === f.value}
                  className={`tab${accountFilter === f.value ? ' active' : ''}`}
                  onClick={() => setAccountFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="chip-set">
              {['all', 'A', 'B', 'C'].map((t) => (
                <div
                  key={t}
                  className={`chip${tierFilter === t ? ' active' : ''}`}
                  onClick={() => setTierFilter(t)}
                >
                  {t === 'all' ? 'All tiers' : `Tier ${t}`}
                </div>
              ))}
            </div>
            <div className="tier-bands-note">
              <b>A</b> ≥{config.tierAMin} · <b>B</b> {config.tierBMin}–{config.tierAMin - 1} ·{' '}
              <b>C</b> &lt;{config.tierBMin}
            </div>
            <button type="button" className="btn primary add-target-btn" onClick={() => setShowAddModal(true)}>
              <Plus size={16} aria-hidden="true" />Add target company
            </button>
          </div>

          {accountFilter === 'watchlisted' && targetAccounts.length > 0 && (
            <div className="watchlist-stats-line">
              {watchlistStats.activeCount} active
              {watchlistStats.pausedCount ? ` · ${watchlistStats.pausedCount} paused` : ''}
              {' · last checked '}
              {watchlistStats.lastCheckedAt ? timeAgo(watchlistStats.lastCheckedAt) : 'never'}
            </div>
          )}

          <div className="list-caption">
            {scoringLoading
              ? 'Updating accounts and scores for the selected sources…'
              : `${visibleAccounts.length} of ${scoredAccounts.length} accounts`}
          </div>

          <div className="list-head">
            <div className="sortable" onClick={() => toggleSort('name')}>
              ACCOUNT{sort.key === 'name' && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </div>
            <div>SIGNAL COMPOSITION</div>
            <div className="sortable" onClick={() => toggleSort('score')}>
              SCORE{sort.key === 'score' && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </div>
            <div className="sortable" onClick={() => toggleSort('signals')}>
              SIGNALS{sort.key === 'signals' && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </div>
            <div className="sortable" onClick={() => toggleSort('recent')}>
              LAST ACTIVITY{sort.key === 'recent' && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </div>
            <div></div>
          </div>

          <div className="account-list">
            {scoringLoading && (
              <div className="state-msg">Updating results…</div>
            )}
            {!scoringLoading && pageAccounts.map((a) => (
              <div
                className={`account-row tier-${a.tier.toLowerCase()}${a.pending ? ' pending' : ''}`}
                key={a.accountId}
                onClick={() => { if (!a.pending) navigate(`/accounts/${a.accountId}`); }}
                title={a.pending ? 'Added to the watchlist — no signals found yet' : undefined}
              >
                <div className="company-cell">
                  <div
                    className={`stamp ${a.tier.toLowerCase()}`}
                    title={`Tier ${a.tier}: score ${tierRangeLabel(a.tier, config)}`}
                  >
                    TIER {a.tier}
                  </div>
                  <div>
                    <div className="company-name">
                      {a.isWatchlisted && (
                        <span className="target-icon" title="Target account">
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
                            <circle cx="8" cy="8" r="3.4" stroke="currentColor" strokeWidth="1.4" />
                            <circle cx="8" cy="8" r="1" fill="currentColor" />
                          </svg>
                        </span>
                      )}
                      {a.accountName}
                    </div>
                    <div className="company-meta">
                      {[a.account.domain].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
                <div className="comp-bar-cell">
                  <div className="comp-bar">
                    <div
                      className="comp-seg"
                      style={{
                        width: `${Math.max(2, a.score)}%`,
                        background: `var(--${a.tier === 'A' ? 'green-deep' : a.tier === 'B' ? 'green' : 'slate'})`,
                      }}
                    />
                  </div>
                </div>
                <div className="score-cell">
                  <span>{a.score.toFixed(0)}</span>
                  <span className="of100"> / 100</span>
                </div>
                <div className="badge-count">
                  {a.signalCount} signal{a.signalCount === 1 ? '' : 's'}
                  {a.corroborated && <span className="corroborated-flag"> · corroborated</span>}
                </div>
                <div className="last-signal">
                  {a.lastSignal ? (
                    <>
                      {timeAgo(eventDate(a.lastSignal))}
                      {isEventDateEstimated(a.lastSignal) && <span title="No published date from the source — this is when we collected it, not necessarily when it happened."> (collected)</span>}
                      <div className="cat">{a.lastSignal.signal_type.replace(/_/g, ' ')} · {sourceLabel(a.lastSignal.source)}</div>
                    </>
                  ) : (
                    <span className="muted-cell">Pending — no signals yet</span>
                  )}
                </div>
                <div className="row-chevron">{a.pending ? '' : '›'}</div>
              </div>
            ))}
            {!scoringLoading && visibleAccounts.length === 0 && scoredAccounts.length > 0 && (
              <div className="empty-state">
                <div className="title">No accounts match these filters</div>
                Widen source coverage or adjust the scoring weights under Advanced options.
              </div>
            )}
            {!scoringLoading && scoredAccounts.length === 0 && (
              <div className="empty-state">
                <div className="title">No scored accounts yet</div>
                Either no signals have been ingested, or every ingested signal is unattributed
                (no account match) — click "Run ingestion" above to populate real data,
                or "+ Add target company" to watch a specific company.
              </div>
            )}
          </div>
        </main>
      </div>
          {!scoringLoading && <Pager page={shownPage} total={visibleAccounts.length} onPage={setPage} />}

      {accountId && <AccountDrawer enabledSources={enabledSources} />}
      {showAddModal && (
        <AddAccountModal
          onClose={() => setShowAddModal(false)}
          onAdded={loadTargetAccounts}
        />
      )}
    </>
  );
}
